import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Select } from '/imports/ui/Select';
import { useMethod } from '/imports/hooks';
import { listInventoryPolicies, InventoryPolicy } from '/imports/api/inventoryPolicies';

/**
 * Dropdown of Inventory Policies, with a summary of the selected policy and a
 * jump-off to create a new one. Same shape as CardListPicker: references stay
 * ergonomic because a one-off policy is one extra click away.
 */
export const InventoryPolicyPicker = ({ value, onChange, inheritLabel }: {
  value: string | null | undefined;
  onChange(policyId: string | null): void;
  /** What "blank" means at this level, e.g. 'No policy — sellCap/stocksize'. */
  inheritLabel: string;
}) => {
  const [res] = useMethod(listInventoryPolicies, { limit: 500, sort: { name: 1 } }, []);
  const policies = (res?.documents ?? []) as InventoryPolicy[];

  const options = useMemo(() => ([
    { value: '', label: inheritLabel },
    ...policies.map((p) => ({ value: p._id, label: p.name })),
  ]), [policies, inheritLabel]);

  const selected = policies.find((p) => p._id === value);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Select className="filter" value={value ?? ''} options={options} onChange={(v) => onChange((v as string) || null)} />
        {selected && (
          <Link className="text-caption text-primary hover:underline" to={`/inventory-policies/${selected._id}`}>
            edit policy
          </Link>
        )}
        <Link className="text-caption text-fg-3 hover:text-fg-1 hover:underline" to="/inventory-policies/new">
          + new policy
        </Link>
      </div>
      {selected && (
        <div className="text-caption text-fg-3">
          {(selected.rules?.length ?? 0)} rule{(selected.rules?.length ?? 0) === 1 ? '' : 's'}
          {selected.defaultQty !== null && selected.defaultQty !== undefined
            ? `, default qty ${selected.defaultQty}`
            : ', no default qty (falls through to sellCap/stocksize)'}
          {selected.defaultAdQty !== null && selected.defaultAdQty !== undefined
            ? `, default ad ${selected.defaultAdQty}`
            : ''}
        </div>
      )}
    </div>
  );
};
